import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import RevealHeading from '../common/RevealHeading';
import useParallax from '../common/useParallax';

const PremiumProjects = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const { ref: parallaxRef, y: parallaxY } = useParallax(60);

  const projects = [
    {
      title: 'Fruit Disease Detection & Quality Prediction',
      category: 'AI / Computer Vision',
      description: 'Image classification model to detect fruit diseases and predict quality grades using CNNs.',
      technologies: ['TensorFlow', 'OpenCV', 'Python', 'CNN', 'Flask'],
      impact: '92% Accuracy',
      github: null,
      demo: null,
      featured: true,
    },
    {
      title: 'AI Chatbot Assistant',
      category: 'NLP / Full Stack',
      description: 'Conversational AI system with an NLP pipeline, React frontend, and Node.js backend.',
      technologies: ['NLP', 'Transformers', 'React.js', 'Node.js', 'MongoDB'],
      impact: 'Full Stack AI App',
      github: 'https://github.com/HARISMUGHAL/AI-agent',
      demo: null,
      featured: true,
    },
    {
      title: 'YOLO Object Detection System',
      category: 'Computer Vision',
      description: 'Real-time object detection using YOLOv5 with custom-trained weights for specific use cases.',
      technologies: ['YOLO', 'OpenCV', 'Python', 'PyTorch', 'REST API'],
      impact: '95% Precision',
      github: null,
      demo: null,
      featured: true,
    },
    {
      title: 'Audio Classification Model',
      category: 'Deep Learning',
      description: 'CNN-based audio classifier trained on mel spectrograms for sound event detection.',
      technologies: ['Librosa', 'TensorFlow', 'Python', 'Spectrogram', 'Keras'],
      impact: '88% Accuracy',
      github: 'https://github.com/HARISMUGHAL/AudioModel',
      demo: null,
    },
    {
      title: 'Stock Market Prediction',
      category: 'ML / Time Series',
      description: 'LSTM-based model for stock price forecasting using historical data and technical indicators.',
      technologies: ['LSTM', 'TensorFlow', 'Pandas', 'TA-Lib', 'Python'],
      impact: 'Time Series Forecasting',
      github: 'https://github.com/HARISMUGHAL/Stock_Predictor',
      demo: null,
    },
    {
      title: 'Author Profiling (NLP)',
      category: 'NLP / ML',
      description: 'NLP pipeline that predicts author demographics and writing style from text samples.',
      technologies: ['spaCy', 'scikit-learn', 'NLTK', 'Python', 'Pandas'],
      impact: '85% Accuracy',
      github: 'https://github.com/HARISMUGHAL/Author-Profiling-using-NLP-and-ML-pipeline-',
      demo: null,
    },
    {
      title: 'Full Stack Web Application',
      category: 'Full Stack',
      description: 'Production-grade web application with React frontend, Express backend, and MongoDB.',
      technologies: ['React.js', 'Node.js', 'MongoDB', 'Express', 'JWT Auth'],
      impact: 'Full Stack App',
      github: null,
      demo: null,
    },
    {
      title: 'Flutter Mobile App',
      category: 'Mobile Development',
      description: 'Cross-platform mobile application built with Flutter, Dart, and Firebase backend.',
      technologies: ['Flutter', 'Dart', 'Firebase', 'REST API', 'UI/UX'],
      impact: 'Cross-Platform App',
      github: 'https://github.com/HARISMUGHAL/Fyp-Frontend',
      demo: null,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  const renderProjectLinks = (project) => {
    const hasGithub = project.github && project.github !== '#';
    const hasDemo = project.demo && project.demo !== '#';

    if (!hasGithub && !hasDemo) return null;

    return (
      <div className="flex items-center gap-3 mt-auto">
        {hasGithub && (
          <motion.a
            href={project.github}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-500 hover:bg-primary-500 hover:text-dark-900 transition-all duration-300 text-sm font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            <FiGithub className="text-sm" />
            Code
          </motion.a>
        )}
        {hasDemo && (
          <motion.a
            href={project.demo}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-accent-500/10 text-accent-500 hover:bg-accent-500 hover:text-dark-900 transition-all duration-300 text-sm font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            <FiExternalLink className="text-sm" />
            Live Demo
          </motion.a>
        )}
      </div>
    );
  };

  return (
    <section id="projects" ref={parallaxRef} className="py-20 lg:py-28 relative overflow-hidden">
      {/* Background */}
      <motion.div
        style={{ y: parallaxY }}
        animate={{ opacity: [0.1, 0.3, 0.1] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute bottom-0 right-0 w-96 h-96 bg-accent-500 rounded-full blur-3xl opacity-5"
      />

      <div className="container-custom relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <RevealHeading text="Featured" accent="Projects" className="text-4xl lg:text-5xl font-bold text-white mb-4" />
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            A selection of projects across AI, full stack, and mobile development
          </p>
        </motion.div>

        {/* Featured Projects */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12"
        >
          {projects
            .filter((p) => p.featured)
            .map((project, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                onMouseEnter={() => setHoveredProject(`featured-${idx}`)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => project.github && window.open(project.github, '_blank')}
                className="group cursor-pointer h-full"
                whileHover={{ y: -15 }}
              >
                <div
                  className="relative h-full rounded-2xl overflow-hidden p-6 transition-all duration-300"
                  style={{
                    perspective: '1000px',
                    transformStyle: 'preserve-3d',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `rotateX(${y * 10}deg) rotateY(${x * 10}deg) translateZ(25px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0) rotateY(0) translateZ(0)';
                  }}
                >
                  {/* Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/15 to-accent-500/15 group-hover:from-primary-500/25 group-hover:to-accent-500/25 transition-all duration-300" />
                  <div className="absolute inset-0 bg-dark-800/50 backdrop-blur-sm group-hover:bg-dark-800/40 transition-all duration-300" />

                  {/* Badge */}
                  <motion.div
                    animate={{
                      y: hoveredProject === `featured-${idx}` ? -5 : 0,
                    }}
                    className="relative mb-4 inline-block px-3 py-1 rounded-full bg-primary-500/30 border border-primary-500/50"
                  >
                    <span className="text-xs font-bold text-primary-300">{project.category}</span>
                  </motion.div>

                  {/* Content */}
                  <div className="relative flex flex-col h-full">
                    <h3 className="text-xl lg:text-2xl font-bold text-white mb-2 group-hover:text-primary-500 transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-white/70 text-sm mb-4 flex-grow">{project.description}</p>

                    {/* Impact */}
                    <div className="mb-4 p-3 rounded-lg bg-accent-500/10 border border-accent-500/20">
                      <p className="text-accent-400 font-semibold text-sm">{project.impact}</p>
                    </div>

                    {/* Technologies */}
                    <div className="flex flex-wrap gap-2 mb-6">
                      {project.technologies.slice(0, 3).map((tech, techIdx) => (
                        <span
                          key={techIdx}
                          className="px-2 py-1 rounded text-xs bg-primary-500/20 text-primary-300 border border-primary-500/30"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Links */}
                    {renderProjectLinks(project)}
                  </div>

                  {/* Border Glow */}
                  <div className="absolute inset-0 rounded-2xl border border-primary-500/0 group-hover:border-primary-500/50 transition-all duration-300" />
                </div>
              </motion.div>
            ))}
        </motion.div>

        {/* All Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {projects
            .filter((p) => !p.featured)
            .map((project, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                onMouseEnter={() => setHoveredProject(`other-${idx}`)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => project.github && window.open(project.github, '_blank')}
                className="group cursor-pointer"
                whileHover={{ x: 10 }}
              >
                <div
                  className="relative rounded-xl overflow-hidden p-5 transition-all duration-300"
                  style={{
                    perspective: '800px',
                    transformStyle: 'preserve-3d',
                  }}
                  onMouseMove={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = (e.clientX - rect.left) / rect.width - 0.5;
                    const y = (e.clientY - rect.top) / rect.height - 0.5;
                    e.currentTarget.style.transform = `rotateX(${y * 5}deg) rotateY(${x * 5}deg) translateZ(10px)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'rotateX(0) rotateY(0) translateZ(0)';
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10 group-hover:from-primary-500/20 group-hover:to-accent-500/20 transition-all duration-300" />
                  <div className="absolute inset-0 bg-dark-800/40 backdrop-blur-sm group-hover:bg-dark-800/30 transition-all duration-300" />

                  <div className="relative flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-white mb-1 group-hover:text-primary-500 transition-colors">
                        {project.title}
                      </h4>
                      <p className="text-xs text-primary-400 mb-2">{project.category}</p>
                      <p className="text-sm text-white/60 mb-3">{project.description}</p>
                      <div className="flex flex-wrap gap-1 mb-3">
                        {project.technologies.slice(0, 3).map((tech, techIdx) => (
                          <span key={techIdx} className="text-xs px-2 py-1 rounded bg-accent-500/10 text-accent-400">
                            {tech}
                          </span>
                        ))}
                      </div>
                      {renderProjectLinks(project)}
                    </div>
                  </div>

                  <div className="absolute inset-0 rounded-xl border border-primary-500/0 group-hover:border-primary-500/30 transition-all duration-300" />
                </div>
              </motion.div>
            ))}
        </motion.div>
        {/* GitHub CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <p className="text-white/50 text-sm mb-4">Want to see more? All projects are available on GitHub.</p>
          <motion.a
            href="https://github.com/HARISMUGHAL"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-dark-800/50 border border-primary-500/30 text-primary-500 font-bold hover:bg-primary-500 hover:text-dark-900 hover:border-primary-500 transition-all duration-300"
          >
            <FiGithub className="text-xl" />
            View All Projects on GitHub
            <FiExternalLink className="text-sm" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumProjects;
